import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { Icon } from '../components/Icon';
import { ReaderControls } from '../components/ReaderControls';
import { VerseList } from '../components/VerseList';
import { getSurahByRoute, loadQuizData, loadSurahByRoute, surahs } from '../data/quran';
import { useInitialContent } from '../lib/content';
import { useAudioController } from '../lib/useAudioController';
import { useReaderSettings } from '../lib/storage';
import { usePageMeta } from '../lib/seo';

const Quiz = lazy(() => import('../components/Quiz').then((module) => ({ default: module.Quiz })));

export function SurahPage() {
  const { surahSlug } = useParams();
  const meta = getSurahByRoute(surahSlug);
  const { initialSurah } = useInitialContent();
  const [surah, setSurah] = useState(() => initialSurah?.number === meta?.number ? initialSurah : null);

  useEffect(() => {
    let active = true;
    if (!meta) return () => { active = false; };
    if (initialSurah?.number === meta.number) {
      setSurah(initialSurah);
      return () => { active = false; };
    }
    setSurah(null);
    loadSurahByRoute(surahSlug).then((data) => { if (active) setSurah(data); });
    return () => { active = false; };
  }, [initialSurah, meta, surahSlug]);

  if (!meta) return <Navigate to="/404" replace />;
  if (!surah) return <section className="loading-page" role="status"><div className="loading-spinner" /><p>Soera laden…</p></section>;
  return <SurahPageContent key={surah.number} surah={surah} />;
}

function SurahPageContent({ surah }) {
  const [tab, setTab] = useState('read');
  const [settings, setSettings] = useReaderSettings();
  const [quizQuestions, setQuizQuestions] = useState([]);
  const audio = useAudioController(surah, settings.reciter);
  const position = surahs.findIndex((item) => item.number === surah.number);
  const previous = surahs[position - 1];
  const next = surahs[position + 1];

  useEffect(() => {
    let active = true;
    if (tab === 'quiz' && !quizQuestions.length) {
      loadQuizData().then((data) => { if (active) setQuizQuestions(data.bySurah[surah.number]); });
    }
    return () => { active = false; };
  }, [quizQuestions.length, surah.number, tab]);

  usePageMeta({
    title: `Soera ${surah.latinName} (${surah.number}) leren — Arabisch, Nederlands & audio`,
    description: `Lees en leer soera ${surah.latinName} (${surah.number}), ${surah.meaning}: ${surah.verseCount} verzen met Uthmani-Arabisch, Nederlandse vertaling, transliteratie en audio.`,
    path: surah.path,
  });

  return (
    <>
      <audio ref={audio.audioRef} preload="none" {...audio.audioProps} />
      <section className="surah-hero pattern-section">
        <div className="container">
          <nav className="breadcrumbs" aria-label="Kruimelpad"><Link to="/"><Icon name="home" /> Home</Link><span>/</span><span>Soera {surah.number}</span></nav>
          <div className="surah-heading">
            <div>
              <span className="eyebrow">Soera {surah.number} · {surah.revelationType === 'Meccan' ? 'Mekkaans' : 'Medinees'}</span>
              <h1>{surah.latinName}</h1>
              <p>{surah.meaning} <span>· {surah.verseCount} verzen</span></p>
            </div>
            <p className="surah-heading-arabic" dir="rtl" lang="ar">{surah.arabicName}</p>
          </div>
        </div>
      </section>

      <div className="container surah-layout">
        <div className="mode-tabs" role="tablist" aria-label="Oefenmodus">
          <button type="button" role="tab" aria-selected={tab === 'read'} onClick={() => setTab('read')}><Icon name="book" /> Lezen</button>
          <button type="button" role="tab" aria-selected={tab === 'quiz'} onClick={() => setTab('quiz')}><Icon name="quiz" /> Quiz</button>
        </div>

        {tab === 'read' && (
          <div role="tabpanel">
            <ReaderControls settings={settings} setSettings={setSettings} audio={audio} />
            <VerseList surah={surah} audio={audio} />
          </div>
        )}
        {tab === 'quiz' && <div role="tabpanel" className="surah-quiz-panel"><Suspense fallback={<PanelLoading />}><Quiz questions={quizQuestions} loading={!quizQuestions.length} title={`Quiz over ${surah.latinName}`} description="Herken de betekenis, de versvolgorde en de soera aan de hand van de getoonde tekst." limit={6} /></Suspense></div>}

        <nav className="surah-pagination" aria-label="Vorige en volgende soera">
          {previous ? <Link to={previous.path}><Icon name="arrow" className="icon-left" /><span><small>Vorige</small>{previous.latinName}</span></Link> : <span />}
          {next ? <Link to={next.path}><span><small>Volgende</small>{next.latinName}</span><Icon name="arrow" /></Link> : <Link to="/"><span><small>Klaar</small>Alle soera’s</span><Icon name="arrow" /></Link>}
        </nav>
      </div>
    </>
  );
}

function PanelLoading() {
  return <div className="panel-loading" role="status"><div className="loading-spinner" /><span>Oefening laden…</span></div>;
}
